import Card from '@/components/ui/Card'
import { useTranslation } from 'react-i18next'

interface Ayah {
  number: number
  text: string
}

interface ReadingViewProps {
  ayahs: Ayah[]
  highlightedAyah?: number
  highlightedWord?: number
}

export default function ReadingView({ ayahs, highlightedAyah, highlightedWord }: ReadingViewProps) {
  const { t } = useTranslation()
  
  const splitIntoWords = (text: string): string[] => {
    // Split Arabic text by spaces while preserving diacritics
    if (!text || typeof text !== 'string') return []
    return text.split(/\s+/).filter((word) => word.length > 0)
  }

  if (!ayahs || ayahs.length === 0) {
    return (
      <Card className="p-8 md:p-10">
        <p className="text-center text-gray-500 dark:text-gray-400">{t('quran.noAyahs', 'لا توجد آيات للعرض')}</p>
      </Card>
    )
  }

  return (
    <Card className="p-8 md:p-10">
      <div className="space-y-6 w-full">
        {ayahs.map((ayah) => {
          if (!ayah || !ayah.text) return null
          
          const words = splitIntoWords(ayah.text)
          const isAyahHighlighted = highlightedAyah === ayah.number

          return (
            <div
              key={ayah.number}
              id={`ayah-${ayah.number}`}
              className={`flex gap-4 items-start transition-all w-full ${
                isAyahHighlighted ? 'bg-primary-50 dark:bg-primary-900/20 -mx-2 px-2 py-2 rounded' : ''
              }`}
            >
              <span className="text-primary-600 dark:text-primary-400 font-bold text-lg flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                {ayah.number}
              </span>
              <p 
                className="text-2xl text-gray-900 dark:text-gray-100 leading-relaxed flex-1 text-right min-w-0 w-full" 
                dir="rtl"
                style={{ 
                  overflowWrap: 'normal',
                  wordBreak: 'normal',
                  whiteSpace: 'normal',
                  maxWidth: '100%',
                  overflow: 'visible',
                  wordSpacing: 'normal',
                  lineHeight: '1.8',
                  textAlign: 'right'
                }}
              >
                {words.length > 0 ? (
                  words.map((word, index) => {
                    const isWordHighlighted = isAyahHighlighted && highlightedWord === index

                    return (
                      <span 
                        key={`${ayah.number}-${index}`} 
                        className="inline-block"
                        style={{ 
                          wordBreak: 'keep-all'
                        }}
                      >
                        <span
                          id={`word-${ayah.number}-${index}`}
                          className={`inline transition-all duration-200 ${
                            isWordHighlighted
                              ? 'bg-primary-400 text-white px-1 rounded scale-105 shadow-md'
                              : ''
                          }`}
                        >
                          {word}
                        </span>
                        {index < words.length - 1 && '\u00A0'}
                      </span>
                    )
                  })
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">{t('quran.ayahTextMissing', 'نص الآية غير متاح')}</span>
                )}
              </p>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
